import { PASSKEY_REGISTER_OPTIONS_API_ROUTE } from '~/routes/remix-api.user.passkey-register-options';

/**
 * A minted WebAuthn creation challenge, with the moment it was issued.
 *
 * Single use — verifying consumes the challenge — and short lived, so the time
 * travels with it: whoever holds one has to know when it stopped being usable.
 */
export interface PasskeyEnrolmentMint {
    optionsJSON: string;
    mintedAt: number;
}

/** The server-side challenge lives ~5 min; re-mint before it can expire. */
export const MINT_MAX_AGE_MS = 4 * 60 * 1000;

/** True once the mint is too old to spend, so the caller mints again first. */
export const isMintStale = (mint: PasskeyEnrolmentMint, now = Date.now()) =>
    now - mint.mintedAt >= MINT_MAX_AGE_MS;

/**
 * Mint creation options for the session member.
 *
 * Throws the server's client-safe message on refusal — an account the policy
 * excludes, a session it cannot see, an outage. Whether that refusal is worth
 * showing the reader depends on where the ask is in the flow, so it is raised
 * rather than swallowed here.
 */
export const mintEnrolmentOptions = async (): Promise<PasskeyEnrolmentMint> => {
    const response = await fetch(PASSKEY_REGISTER_OPTIONS_API_ROUTE, {
        method: 'POST',
    });
    const body = (await response.json()) as
        | { ok: true; optionsJSON: string }
        | { ok: false; message: string };
    if (!body.ok) {
        throw new Error(body.message);
    }

    return { optionsJSON: body.optionsJSON, mintedAt: Date.now() };
};
